import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Asterisk, Instagram } from "lucide-react";
import { api, inr } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1571513800374-df1bbe650e56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxoaWdoJTIwZmFzaGlvbiUyMGVkaXRvcmlhbCUyMHdvbWFufGVufDB8fHx8MTc4NzA0NzcwMXww&ixlib=rb-4.1.0&q=85";
const MANIFESTO_IMG =
  "https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxoaWdoJTIwZmFzaGlvbiUyMGVkaXRvcmlhbCUyMHdvbWFufGVufDB8fHx8MTc4NzA0NzcwMXww&ixlib=rb-4.1.0&q=85";

const INSTAGRAM_URL = process.env.REACT_APP_INSTAGRAM_URL || "https://instagram.com/hunardesigns";
const INSTA_IMAGES = [
  "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?q=80&w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?q=80&w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=800&auto=format&fit=crop",
];

const lineWrap = { hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } } };
const line = { hidden: { y: "115%" }, show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };

const chapters = [
  {
    n: "01",
    title: "Two worlds, one hunar",
    copy: "Wardrobe and living room, curated side by side. Because personal style doesn't stop at what you wear.",
  },
  {
    n: "02",
    title: "Craft above all",
    copy: "Hand-block prints, honest cotton, artisan quilting. Every piece is chosen for how it's made — and how long it lasts.",
  },
  {
    n: "03",
    title: "Ordering, effortless",
    copy: "See something you love? One tap opens WhatsApp and you're talking to us directly. No carts, no checkout maze.",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 90]);
  const markY = useTransform(scrollY, [0, 900], [0, -60]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 pb-20 pt-16 md:px-12 lg:px-20 lg:pt-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-xs font-bold uppercase tracking-[0.25em] text-soot"
            >
              Hunar Designs · Fashion · Furnishing
            </motion.p>
            <motion.h1
              variants={lineWrap}
              initial="hidden"
              animate="show"
              data-testid="hero-heading"
              className="mt-6 font-serif text-6xl font-light leading-[0.95] tracking-tighter text-obsidian sm:text-7xl lg:text-8xl xl:text-[7.5rem]"
            >
              {["Style for You,", "Style for Home."].map((l) => (
                <span key={l} className="block overflow-hidden pb-1">
                  <motion.span variants={line} className="block">
                    {l}
                  </motion.span>
                </span>
              ))}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.6 }}
              className="mt-8 max-w-md text-base leading-relaxed text-soot"
            >
              Fashion wear and home furnishing under one roof — browse the collections, and order anything you love in a single WhatsApp message.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.75 }} className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop?section=fashion"
                data-testid="hero-shop-fashion-button"
                className="group inline-flex items-center gap-3 rounded-full bg-obsidian px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:bg-olive"
              >
                Shop Fashion
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/shop?section=furnishing"
                data-testid="hero-shop-home-button"
                className="inline-flex items-center gap-3 rounded-full border border-obsidian px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-obsidian transition-colors duration-300 hover:bg-obsidian hover:text-bone"
              >
                Shop Home
              </Link>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: heroY }} className="relative">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
                className="overflow-hidden rounded-t-[200px] md:rounded-t-[260px]"
              >
                <motion.img
                  src={HERO_IMG}
                  alt="Editorial fashion — marigold jacket"
                  data-testid="hero-image"
                  initial={{ scale: 1.25 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
                  className="aspect-[3/4] w-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.15, duration: 0.7 }}
                className="absolute -bottom-6 -left-4 border border-seam bg-bone/90 px-5 py-4 backdrop-blur-md md:-left-8"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-soot">The Marigold Edit</p>
                <p className="font-serif text-lg italic">New this season</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <motion.span
          style={{ y: markY }}
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-10 left-0 select-none whitespace-nowrap font-serif text-[18vw] leading-none text-transparent opacity-60"
        >
          <span style={{ WebkitTextStroke: "1px #D1CEC5" }}>HUNAR</span>
        </motion.span>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-bone/10 bg-obsidian py-5 text-bone" data-testid="editorial-marquee">
        <Marquee speed={30} gradient={false}>
          {["Fashion Wear", "Home Furnishing", "Mumkey Maternity", "Order on WhatsApp"].map((t) => (
            <span key={t} className="mx-8 flex items-center gap-16 font-serif text-2xl italic md:text-3xl">
              {t}
              <Asterisk className="h-5 w-5" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* FEATURED BENTO */}
      <section className="border-t border-seam/60 px-6 py-24 md:px-12 lg:px-20 lg:py-32" data-testid="featured-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-4xl font-light tracking-tight sm:text-5xl md:text-6xl"
            >
              The Featured Edit
            </motion.h2>
            <Link
              to="/shop"
              data-testid="featured-view-all-link"
              className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-soot transition-colors hover:text-obsidian"
            >
              View All
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid auto-rows-[230px] grid-cols-1 gap-4 md:auto-rows-[260px] md:grid-cols-12">
            {featured.slice(0, 4).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`${
                  i === 0 ? "max-md:row-span-2 md:col-span-7 md:row-span-2" : i === 3 ? "md:col-span-12" : "md:col-span-5"
                }`}
              >
                <Link
                  to={`/product/${p.id}`}
                  data-testid={`featured-card-${p.id}`}
                  className="group relative block h-full w-full overflow-hidden bg-linen"
                >
                  {p.images?.[0] && (
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-obsidian/0 transition-colors duration-500 group-hover:bg-obsidian/25" />
                  <div className="absolute bottom-0 left-0 flex w-full items-end justify-between gap-3 p-5">
                    <div className="bg-bone/90 px-4 py-3 backdrop-blur-md">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-soot">
                        {p.subcategory || (p.category === "fashion" ? "Fashion Wear" : "Home Furnishing")}
                      </p>
                      <p className="font-serif text-xl leading-tight">{p.name}</p>
                      <p className="mt-1 text-xs font-semibold">{inr(p.price)}</p>
                    </div>
                    <span className="flex items-center gap-2 bg-obsidian px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-bone opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      View <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-obsidian px-6 py-24 text-bone md:px-12 lg:px-20 lg:py-36" data-testid="manifesto-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="mb-16 grid gap-8 lg:grid-cols-2 lg:items-end">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-serif text-4xl font-light tracking-tight sm:text-5xl md:text-6xl"
            >
              Three quiet promises.
            </motion.h2>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-bone/50 lg:text-right">
              The Manifesto — Hunar Designs
            </p>
          </div>
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden rounded-b-[180px] md:rounded-b-[240px]"
              >
                <img src={MANIFESTO_IMG} alt="Monochrome editorial fashion" loading="lazy" className="aspect-[3/4] w-full object-cover" />
              </motion.div>
            </div>
            <div className="flex flex-col justify-center gap-12 lg:col-span-7">
              {chapters.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="grid gap-4 border-t border-bone/15 pt-8 sm:grid-cols-12"
                  data-testid={`manifesto-chapter-${c.n}`}
                >
                  <span className="font-serif text-6xl font-light text-bone/25 sm:col-span-3">{c.n}</span>
                  <div className="sm:col-span-9">
                    <h3 className="font-serif text-2xl md:text-3xl">{c.title}</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-bone/60">{c.copy}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="border-t border-seam/60 px-6 py-24 md:px-12 lg:px-20" data-testid="instagram-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-serif text-4xl font-light tracking-tight sm:text-5xl"
            >
              Follow the <span className="italic">Feed</span>
            </motion.h2>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-link"
              className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-soot transition-colors hover:text-obsidian"
            >
              <Instagram className="h-4 w-4" />
              @hunardesigns
            </a>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {INSTA_IMAGES.map((img, i) => (
              <motion.a
                key={i}
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-tile-${i}`}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative aspect-square overflow-hidden bg-linen"
              >
                <img
                  src={img}
                  alt="Hunar Designs on Instagram"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-obsidian/0 transition-colors duration-500 group-hover:bg-obsidian/30">
                  <Instagram className="h-7 w-7 text-bone opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-linen px-6 py-28 md:px-12 lg:px-20" data-testid="closing-cta">
        <div className="mx-auto max-w-[1400px]">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs font-bold uppercase tracking-[0.25em] text-soot"
          >
            Begin browsing
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
          >
            Dress the part. <span className="italic">Live it too.</span>
          </motion.h2>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="mt-10">
            <Link
              to="/shop"
              data-testid="cta-shop-button"
              className="group inline-flex items-center gap-3 rounded-full bg-obsidian px-10 py-5 text-xs font-bold uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:bg-olive"
            >
              Shop the Collections
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
